import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import categoryService from "./categoryService";

const errMsg = (error) =>
  error.response?.data?.message || error.message || "Something went wrong";

export const fetchCategories = createAsyncThunk(
  "category/fetchAll",
  async (params, thunkAPI) => {
    try {
      const res = await categoryService.getAll(params);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { message: errMsg(error) });
    }
  }
);

export const createCategory = createAsyncThunk(
  "category/create",
  async (formData, thunkAPI) => {
    try {
      const res = await categoryService.create(formData);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { message: errMsg(error) });
    }
  }
);

export const updateCategory = createAsyncThunk(
  "category/update",
  async (formData, thunkAPI) => {
    try {
      const res = await categoryService.update(formData);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { message: errMsg(error) });
    }
  }
);

export const deleteCategory = createAsyncThunk(
  "category/delete",
  async (id, thunkAPI) => {
    try {
      await categoryService.remove(id);
      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { message: errMsg(error) });
    }
  }
);

const categorySlice = createSlice({
  name: "category",
  initialState: {
    list: [],
    isLoading: false,   // list load mate
    isSaving: false,    // create/update/delete mate
    isError: false,
    message: "",
    errors: {},         // field wise validation errors
  },
  reducers: {
    clearCategoryError: (state) => {
      state.isError = false;
      state.message = "";
      state.errors = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch
      .addCase(fetchCategories.pending, (s) => { s.isLoading = true; })
      .addCase(fetchCategories.fulfilled, (s, a) => { s.isLoading = false; s.list = a.payload; })
      .addCase(fetchCategories.rejected, (s, a) => {
        s.isLoading = false; s.isError = true;
        s.message = a.payload?.message || "Failed to load categories";
      })
      // create
      .addCase(createCategory.pending, (s) => { s.isSaving = true; s.isError = false; s.errors = {}; })
      .addCase(createCategory.fulfilled, (s, a) => { s.isSaving = false; s.list.unshift(a.payload); })
      .addCase(createCategory.rejected, (s, a) => {
        s.isSaving = false; s.isError = true;
        s.message = a.payload?.message || "Create failed";
        s.errors = a.payload?.errors || {};
      })
      // update
      .addCase(updateCategory.pending, (s) => { s.isSaving = true; s.isError = false; s.errors = {}; })
      .addCase(updateCategory.fulfilled, (s, a) => {
        s.isSaving = false;
        s.list = s.list.map((c) => (c.id === a.payload.id ? a.payload : c));
      })
      .addCase(updateCategory.rejected, (s, a) => {
        s.isSaving = false; s.isError = true;
        s.message = a.payload?.message || "Update failed";
        s.errors = a.payload?.errors || {};
      })
      // delete
      .addCase(deleteCategory.pending, (s) => { s.isSaving = true; })
      .addCase(deleteCategory.fulfilled, (s, a) => {
        s.isSaving = false;
        s.list = s.list.filter((c) => c.id !== a.payload);
      })
      .addCase(deleteCategory.rejected, (s, a) => {
        s.isSaving = false; s.isError = true;
        s.message = a.payload?.message || "Delete failed";
      });
  },
});

export const { clearCategoryError } = categorySlice.actions;
export default categorySlice.reducer;